import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Home,
  Search,
  Compass,
  ArrowLeft,
  Zap,
  BarChart3,
  ExternalLink,
  AlertTriangle,
} from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { format } from "date-fns";

function useLog404() {
  const [location] = useLocation();
  useEffect(() => {
    apiRequest("POST", "/api/404-log", {
      path: location,
      referrer: document.referrer || null,
    }).catch(() => {});
  }, [location]);
}

interface NotFoundReport {
  totalHits: number;
  uniquePaths: number;
  period: string;
  paths: { path: string; count: number; lastSeen: string; referrers: string[] }[];
  recentLogs: { id: number; path: string; referrer: string | null; createdAt: string; userId: string | null }[];
}

function AdminReport() {
  const { data, isLoading } = useQuery<NotFoundReport>({
    queryKey: ["/api/admin/404-logs"],
  });

  if (isLoading) {
    return (
      <div className="mt-12 max-w-4xl mx-auto px-4">
        <div className="h-8 w-48 bg-muted animate-pulse rounded mb-4" />
        <div className="h-64 bg-muted animate-pulse rounded" />
      </div>
    );
  }

  if (!data || data.totalHits === 0) {
    return (
      <div className="mt-12 max-w-4xl mx-auto px-4">
        <Card>
          <CardContent className="pt-6 text-center text-muted-foreground">
            <BarChart3 className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No 404 hits recorded in the last 30 days.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mt-12 max-w-4xl mx-auto px-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg">404 Report</CardTitle>
            </div>
            <Badge variant="secondary" className="no-default-active-elevate" data-testid="badge-404-period">
              {data.period}
            </Badge>
          </div>
          <div className="flex gap-4 mt-2">
            <div className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground" data-testid="text-total-hits">{data.totalHits}</span> total hits
            </div>
            <div className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground" data-testid="text-unique-paths">{data.uniquePaths}</span> unique paths
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Path</TableHead>
                  <TableHead className="text-right">Hits</TableHead>
                  <TableHead>Last Seen</TableHead>
                  <TableHead>Referrers</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.paths.slice(0, 25).map((entry) => (
                  <TableRow key={entry.path} data-testid={`row-404-${entry.path}`}>
                    <TableCell>
                      <code className="text-sm bg-muted px-1.5 py-0.5 rounded">{entry.path}</code>
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant={entry.count >= 10 ? "destructive" : entry.count >= 5 ? "secondary" : "outline"} className="no-default-active-elevate">
                        {entry.count}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {format(new Date(entry.lastSeen), "MMM d, h:mm a")}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-0.5">
                        {entry.referrers.length > 0 ? entry.referrers.map((ref, i) => (
                          <span key={i} className="text-xs text-muted-foreground truncate max-w-[200px] flex items-center gap-1">
                            <ExternalLink className="h-2.5 w-2.5 shrink-0" />
                            {ref}
                          </span>
                        )) : (
                          <span className="text-xs text-muted-foreground">Direct</span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function NotFound() {
  const { user } = useAuth();
  const [, navigate] = useLocation();
  useLog404();

  const quickLinks = [
    { label: "Home", href: "/", icon: Home },
    { label: "Discover", href: "/discover", icon: Compass },
    { label: "Network", href: "/network", icon: Zap },
    { label: "Search", href: "/discover/articles", icon: Search },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />

      <main className="flex-1">
        <div className="flex flex-col items-center justify-center py-16 sm:py-24 px-4">
          <div className="relative mb-8">
            <div className="text-[120px] sm:text-[180px] font-black leading-none tracking-tighter text-transparent bg-clip-text bg-gradient-to-br from-primary/20 via-primary/40 to-primary/10 select-none" data-testid="text-404-hero">
              404
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-background/80 backdrop-blur-sm rounded-xl px-6 py-3 border shadow-sm">
                <AlertTriangle className="h-8 w-8 text-primary mx-auto mb-1" />
                <p className="text-sm font-medium text-muted-foreground">Signal Lost</p>
              </div>
            </div>
          </div>

          <h1 className="text-2xl sm:text-3xl font-bold text-foreground text-center mb-3" data-testid="text-404-title">
            This page has gone off the grid
          </h1>
          <p className="text-muted-foreground text-center max-w-md mb-8 text-sm sm:text-base" data-testid="text-404-description">
            Even the best tech leaders hit a dead end sometimes.
            The page you're looking for doesn't exist or may have been moved.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 mb-12">
            <Button
              variant="default"
              size="lg"
              onClick={() => navigate("/")}
              data-testid="button-go-home"
            >
              <Home className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => window.history.back()}
              data-testid="button-go-back"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go Back
            </Button>
          </div>

          <div className="w-full max-w-md">
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3 text-center font-medium">Quick Links</p>
            <div className="grid grid-cols-2 gap-2">
              {quickLinks.map((link) => (
                <Link key={link.href} href={link.href}>
                  <button
                    className="w-full flex items-center gap-2 px-4 py-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors text-left"
                    data-testid={`link-quick-${link.label.toLowerCase()}`}
                  >
                    <link.icon className="h-4 w-4 text-primary shrink-0" />
                    <span className="text-sm font-medium">{link.label}</span>
                  </button>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {user?.isAdmin && <AdminReport />}
      </main>

      <Footer />
    </div>
  );
}
